import type { ThemePalette } from '../../types';
import { DIAGRAM_KIT_IDS, getKit, type DiagramKitId } from '../../utils/diagramKits';

interface EmptyStateProps {
  palette: ThemePalette;
  glass: React.CSSProperties;
  onStartC4: () => void;
  onPasteMermaid: () => void;
  onBlank: () => void;
  onPickKit?: (kit: DiagramKitId) => void;
}

export function EmptyState({
  palette,
  glass,
  onStartC4,
  onPasteMermaid,
  onBlank,
  onPickKit,
}: EmptyStateProps) {
  return (
    <div className="absolute inset-0 z-30 flex items-center justify-center pointer-events-none">
      <div
        className="pointer-events-auto w-[420px] rounded-2xl border shadow-2xl backdrop-blur-md p-6"
        style={glass}
        data-tour="empty-state"
      >
        <div className="text-sm font-semibold" style={{ color: palette.panelText }}>
          Start this page
        </div>
        <p className="text-xs mt-1.5 leading-relaxed" style={{ color: palette.panelSubText }}>
          C4 workspace is the default path: one catalog of components, reused on Context, Containers, and Sequence.
        </p>
        <div className="mt-4 space-y-2">
          <button
            className="w-full text-left px-3.5 py-3 rounded-xl text-sm font-medium transition-colors hover:bg-white/5"
            style={{ color: palette.panelText, background: 'rgba(99,102,241,0.12)', border: '1px solid rgba(99,102,241,0.35)' }}
            onClick={onStartC4}
          >
            Start a C4 workspace
            <span className="block text-[11px] font-normal mt-0.5" style={{ color: palette.panelSubText }}>
              Context → Containers → Sequence, with shared catalog identity
            </span>
          </button>
          <button
            className="w-full text-left px-3.5 py-3 rounded-xl text-sm transition-colors hover:bg-white/5 border"
            style={{ color: palette.panelText, borderColor: palette.panelBorder }}
            onClick={onPasteMermaid}
          >
            Paste Mermaid
            <span className="block text-[11px] mt-0.5" style={{ color: palette.panelSubText }}>
              Import a flowchart or sequence diagram
            </span>
          </button>
          <button
            className="w-full text-left px-3.5 py-3 rounded-xl text-sm transition-colors hover:bg-white/5 border"
            style={{ color: palette.panelText, borderColor: palette.panelBorder }}
            onClick={onBlank}
          >
            Blank canvas
            <span className="block text-[11px] mt-0.5" style={{ color: palette.panelSubText }}>
              Start empty — add nodes, then fill description, owner, and links
            </span>
          </button>
        </div>
        {onPickKit && (
          <div className="mt-4">
            <div className="text-[10px] uppercase tracking-wider mb-1.5" style={{ color: palette.panelSubText }}>
              Or pick a diagram kit
            </div>
            <div className="flex flex-wrap gap-1.5">
              {DIAGRAM_KIT_IDS.map(id => {
                const kit = getKit(id);
                return (
                  <button
                    key={id}
                    className="px-2.5 py-1.5 rounded-lg text-[11px] font-semibold border transition-colors hover:bg-white/5"
                    style={{ color: palette.panelText, borderColor: palette.panelBorder }}
                    title={kit.description}
                    onClick={() => onPickKit(id)}
                  >
                    {kit.label}
                  </button>
                );
              })}
            </div>
            <p className="text-[10px] mt-2 leading-snug" style={{ color: palette.panelSubText }}>
              Same canvas. Kits only change the palette, connections, and Beautify direction — not a new editor.
            </p>
          </div>
        )}
        <p className="text-[10px] mt-4" style={{ color: palette.panelSubText }}>
          Cmd/Ctrl+K opens the command palette · hold I to inspect packed info
        </p>
      </div>
    </div>
  );
}
